import React, { useState, useEffect, useRef } from 'react';
import { FileText, ChevronRight, Download, ArrowUp, CheckCircle, AlertTriangle, Users, Database, Lock, Scale, Calendar, Mail, Shield, Gavel, Eye, UserCheck } from 'lucide-react';

import Header from '../component/layout/Header';
import Footer from '../component/layout/Footer';

const TermsConditionsPage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Handle scroll for show scroll-to-top button and active section
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 500);

            // Update active section based on scroll position
            const sections = document.querySelectorAll('[data-section]');
            let current = '';

            sections.forEach((section) => {
                const sectionTop = section.offsetTop;
                const sectionHeight = section.clientHeight;
                if (window.scrollY >= sectionTop - 200) {
                    current = section.getAttribute('data-section');
                }
            });

            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToSection = (sectionId) => {
        const element = document.querySelector(`[data-section="${sectionId}"]`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const sections = [
        { id: 'introduction', title: 'Introduction', icon: FileText },
        { id: 'acceptance', title: 'Acceptance of Terms', icon: CheckCircle },
        { id: 'eligibility', title: 'Eligibility', icon: UserCheck },
        { id: 'ownership', title: 'Ownership of Rights', icon: Lock },
        { id: 'access-usage', title: 'Access & Usage', icon: Eye },
        { id: 'indemnity', title: 'Indemnity & Release', icon: Scale },
        { id: 'disclaimer', title: 'Disclaimer', icon: AlertTriangle },
        { id: 'privacy', title: 'Privacy Information', icon: Shield },
        { id: 'contact', title: 'Contact Us', icon: Mail }
    ];

    const SectionCard = ({ section, children, delay = 0 }) => (
        <div
            data-section={section.id}
            className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
            style={{ transitionDelay: `${delay}s` }}
        >
            <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-[#F05A28] rounded-xl flex items-center justify-center mr-4">
                    <section.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                {children}
            </div>
        </div>
    );

    return (
        <>
            <Header />
            <div ref={sectionRef} className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                        >
                            <div className="flex items-center mb-6">
                                <Gavel className="w-8 h-8 mr-4" />
                                <span className="text-blue-100 font-semibold tracking-wider text-sm uppercase">
                                    Legal Agreement
                                </span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                Terms & Conditions
                            </h1>
                            <p className="text-xl text-blue-100 leading-relaxed max-w-3xl">
                                Please read these terms and conditions carefully before using our services and applications.
                            </p>
                            <div className="mt-8 flex items-center text-blue-100">
                                <Calendar className="w-5 h-5 mr-2" />
                                <span>Last updated: January 10, 2024</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid lg:grid-cols-12 gap-12">

                        {/* Table of Contents - Sidebar */}
                        <div className="lg:col-span-3">
                            <div className="sticky top-8">
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900 mb-6">Contents</h3>
                                    <nav className="space-y-2">
                                        {sections.map((section, index) => (
                                            <button
                                                key={section.id}
                                                onClick={() => scrollToSection(section.id)}
                                                className={`w-full flex items-center text-left p-3 rounded-xl transition-all duration-300 ${activeSection === section.id
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <section.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                                                <span className="text-sm font-medium">{section.title}</span>
                                                <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="lg:col-span-9">

                            {/* Introduction */}
                            <SectionCard section={sections[0]} delay={0.2}>
                                <p className="mb-4">
                                    These Terms & Conditions of Use govern your use of the RoadExpress Freight Mobile Application ("Application"). The Application is owned by RoadExpress Technology Private Limited ("RoadExpress" or "we" or "us").
                                </p>
                                <p className="mb-4">
                                    By accessing or using our Application, you agree to be bound by these Terms and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this Application.
                                </p>
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                                    <p className="text-blue-800 font-medium flex items-center">
                                        <AlertTriangle className="w-5 h-5 mr-2" />
                                        Please read these terms carefully before using our services.
                                    </p>
                                </div>
                            </SectionCard>

                            {/* Acceptance of Terms */}
                            <SectionCard section={sections[1]} delay={0.3}>
                                <p className="mb-4">
                                    Please read these terms & conditions of use carefully. By accessing or using the Application, you are agreeing to the terms & conditions of use set out below (as revised from time to time) ("Terms").
                                </p>
                                <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl mb-4">
                                    <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                                        <AlertTriangle className="w-5 h-5 mr-2" />
                                        Important Notice
                                    </h4>
                                    <p className="text-yellow-800">
                                        The Terms may be updated from time to time, and it is your responsibility to review them regularly. Your continued use of the Application after any changes constitutes acceptance of those changes.
                                    </p>
                                </div>
                                <p className="mb-4">
                                    Subject to your compliance with these Terms, RoadExpress grants you a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to use and access the Application.
                                </p>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                                        <h5 className="font-semibold text-green-900 mb-2">What You Get</h5>
                                        <ul className="text-green-800 text-sm space-y-1">
                                            <li>• Limited usage rights</li>
                                            <li>• Access to our services</li>
                                            <li>• Support and updates</li>
                                        </ul>
                                    </div>
                                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                                        <h5 className="font-semibold text-red-900 mb-2">Restrictions</h5>
                                        <ul className="text-red-800 text-sm space-y-1">
                                            <li>• Non-transferable license</li>
                                            <li>• No sublicensing allowed</li>
                                            <li>• Revocable at any time</li>
                                        </ul>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Eligibility */}
                            <SectionCard section={sections[2]} delay={0.4}>
                                <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl mb-6">
                                    <div className="flex items-start">
                                        <UserCheck className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-blue-900 mb-2">Age Requirement</h4>
                                            <p className="text-blue-800">
                                                You must be over the age of eighteen (18) years and able to understand and agree to the terms, conditions, obligations, representations, and warranties set forth in these Terms.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <p className="mb-4">
                                    You may use the Application in accordance with our contractual arrangement with you and/or as per these Terms. To use our services, you must:
                                </p>
                                <div className="space-y-3">
                                    {[
                                        'Be of legal age (18 years or older)',
                                        'Have the legal capacity to enter into agreements',
                                        'Provide accurate and truthful information',
                                        'Comply with all applicable laws and regulations',
                                        'Not be prohibited from using our services under any applicable law'
                                    ].map((requirement, index) => (
                                        <div key={index} className="flex items-center">
                                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                            <p>{requirement}</p>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>

                            {/* Ownership of Rights */}
                            <SectionCard section={sections[3]} delay={0.5}>
                                <p className="mb-6">
                                    All rights, including copyright, in this application and website, are owned by or licensed to RoadExpress. The application is designed, updated and maintained independently by RoadExpress.
                                </p>

                                <div className="grid gap-6">
                                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                                        <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
                                            <Lock className="w-5 h-5 mr-2" />
                                            Intellectual Property Protection
                                        </h4>
                                        <p className="text-purple-800 mb-4">
                                            All information displayed, transmitted or carried on RoadExpress is protected by copyright and other intellectual property laws.
                                        </p>
                                        <div className="text-purple-800 text-sm">
                                            <p><strong>Protected Content Includes:</strong></p>
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li>Application design and code</li>
                                                <li>Text, graphics, and images</li>
                                                <li>Logos and trademarks</li>
                                                <li>Software and algorithms</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-xl border border-red-200">
                                        <h4 className="font-semibold text-red-900 mb-3 flex items-center">
                                            <AlertTriangle className="w-5 h-5 mr-2" />
                                            Prohibited Activities
                                        </h4>
                                        <p className="text-red-800 mb-4">
                                            Any use of the Application or its contents, other than for your own personal, non-commercial use is prohibited without explicit permission.
                                        </p>
                                        <div className="text-red-800 text-sm">
                                            <p><strong>You may not:</strong></p>
                                            <ul className="list-disc list-inside mt-2 space-y-1">
                                                <li>Modify, publish, or transmit content</li>
                                                <li>Create derivative works</li>
                                                <li>Distribute or reproduce content</li>
                                                <li>Reverse engineer the application</li>
                                                <li>Use content for commercial purposes</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 bg-gray-50 border border-gray-200 p-4 rounded-xl">
                                    <p className="text-gray-800">
                                        <strong>Trademark Notice:</strong> "RoadExpress" is a registered trademark. All other trademarks, company names, logos, and product names referenced are the property of their respective owners.
                                    </p>
                                </div>
                            </SectionCard>

                            {/* Access to and Use of Application */}
                            <SectionCard section={sections[4]} delay={0.6}>
                                <p className="mb-6">
                                    To ensure a safe and secure environment for all users, the following activities are strictly prohibited:
                                </p>

                                <div className="space-y-6">
                                    <div className="border-l-4 border-red-500 pl-6 bg-red-50 p-4 rounded-r-xl">
                                        <h4 className="font-semibold text-red-900 mb-3 flex items-center">
                                            <AlertTriangle className="w-5 h-5 mr-2" />
                                            Unauthorized Access
                                        </h4>
                                        <p className="text-red-800">
                                            You are prohibited from accessing data not intended for you or logging onto a processor, access device, or account which you are not authorized to access.
                                        </p>
                                    </div>

                                    <div className="border-l-4 border-orange-500 pl-6 bg-orange-50 p-4 rounded-r-xl">
                                        <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
                                            <Database className="w-5 h-5 mr-2" />
                                            Service Interference
                                        </h4>
                                        <p className="text-orange-800">
                                            You shall not interfere with or disrupt the services for any other user, including submitting a virus, worm, or other malicious code to the network.
                                        </p>
                                    </div>

                                    <div className="border-l-4 border-yellow-500 pl-6 bg-yellow-50 p-4 rounded-r-xl">
                                        <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                                            <Mail className="w-5 h-5 mr-2" />
                                            Spam Prevention
                                        </h4>
                                        <p className="text-yellow-800">
                                            You shall not use the Application to send any unsolicited email or other information.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6 bg-green-50 border border-green-200 p-6 rounded-xl">
                                    <h4 className="font-semibold text-green-900 mb-3">Acceptable Use</h4>
                                    <p className="text-green-800">
                                        We encourage responsible use of our Application for legitimate logistics and transportation needs.
                                        If you have questions about acceptable use, please contact our support team.
                                    </p>
                                </div>
                            </SectionCard>

                            {/* Indemnity and Release */}
                            <SectionCard section={sections[5]} delay={0.7}>
                                <div className="bg-orange-50 border border-orange-200 p-6 rounded-xl mb-6">
                                    <div className="flex items-start">
                                        <Scale className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-orange-900 mb-2">Your Responsibility</h4>
                                            <p className="text-orange-800">
                                                You shall indemnify and hold us harmless from any claim, demand, or actions, including reasonable attorney's fees, made by any third party due to or arising out of your breach of these Terms.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <p className="mb-6">This indemnification includes but is not limited to:</p>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        {[
                                            'Breach of these Terms',
                                            'Violation of any law or regulation',
                                            'Infringement of third-party rights',
                                            'Misuse of the Application'
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg">
                                                <AlertTriangle className="w-4 h-4 text-orange-500 mr-2 flex-shrink-0" />
                                                <span className="text-gray-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-3">
                                        {[
                                            'Unauthorized access attempts',
                                            'Data security breaches you cause',
                                            'False or misleading information',
                                            'Damage to our reputation'
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg">
                                                <AlertTriangle className="w-4 h-4 text-orange-500 mr-2 flex-shrink-0" />
                                                <span className="text-gray-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Disclaimer */}
                            <SectionCard section={sections[6]} delay={0.8}>
                                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl mb-6">
                                    <h4 className="font-semibold text-red-900 mb-3 flex items-center">
                                        <AlertTriangle className="w-5 h-5 mr-2" />
                                        Important Disclaimer
                                    </h4>
                                    <p className="text-red-800">
                                        Information, services, names, pictures, advertisements, images and contents are provided "as is" on the Application.
                                        RoadExpress expressly disclaims all warranties, whether express or implied.
                                    </p>
                                </div>

                                <div className="grid gap-6">
                                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                                        <h5 className="font-semibold text-gray-900 mb-3">No Warranties</h5>
                                        <p className="text-gray-700 mb-4">
                                            We disclaim all warranties including, but not limited to, implied warranties of merchantability and fitness for a particular purpose.
                                        </p>
                                        <ul className="text-gray-600 text-sm space-y-1">
                                            <li>• No guarantee of uninterrupted service</li>
                                            <li>• No warranty of error-free operation</li>
                                            <li>• No guarantee of data accuracy</li>
                                            <li>• No warranty of security</li>
                                        </ul>
                                    </div>

                                    <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                                        <h5 className="font-semibold text-blue-900 mb-3">User Responsibility</h5>
                                        <p className="text-blue-800">
                                            Parties using the information provided in the Application may do so after satisfying themselves that
                                            the same suits their requirements. The information should not be treated as legal or professional advice.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6 bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
                                    <p className="text-yellow-800 text-sm">
                                        <strong>Note:</strong> RoadExpress uses information voluntarily provided by users to optimize their experience
                                        and prepare better future content, but this information is provided at your own discretion and risk.
                                    </p>
                                </div>
                            </SectionCard>

                            {/* Privacy Information */}
                            <SectionCard section={sections[7]} delay={0.9}>
                                <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl mb-6">
                                    <div className="flex items-start">
                                        <Shield className="w-6 h-6 text-blue-600 mr-3 mt-1" />
                                        <div>
                                            <h4 className="font-semibold text-blue-900 mb-2">Privacy Commitment</h4>
                                            <p className="text-blue-800">
                                                We consider customer trust as our top priority and take the protection of your personal information very seriously.
                                                We want you to feel safe using our products, services and solutions.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <p className="mb-6">Our Privacy Policy covers important topics including:</p>

                                <div className="grid md:grid-cols-2 gap-4 mb-6">
                                    <div className="space-y-3">
                                        {[
                                            'What data we collect',
                                            'When & how we collect your data',
                                            'Why we collect your data',
                                            'Data sharing practices'
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                                                <span className="text-gray-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-3">
                                        {[
                                            'How long we keep your information',
                                            'How we secure your information',
                                            'Your rights as a data subject',
                                            'How to contact us'
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                                                <span className="text-gray-700 text-sm">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="bg-green-50 border border-green-200 p-4 rounded-xl">
                                    <p className="text-green-800">
                                        <strong>Complete Privacy Policy:</strong> Please read our full Privacy Policy for detailed information
                                        about how we handle your personal data and your privacy rights.
                                    </p>
                                </div>
                            </SectionCard>

                            {/* Contact Us */}
                            <SectionCard section={sections[8]} delay={1.0}>
                                <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
                                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                        <Mail className="w-5 h-5 text-[#F05A28] mr-2" />
                                        Questions About These Terms?
                                    </h4>
                                    <p className="text-gray-700 mb-4">
                                        If you have any questions about these Terms & Conditions, please don't hesitate to contact us.
                                        We're here to help clarify any aspects of our agreement.
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center">
                                            <Mail className="w-5 h-5 text-[#F05A28] mr-3" />
                                            <div>
                                                <p className="font-medium text-gray-900">Email Support</p>
                                                <a href="mailto:connect@roadexpressfreight.com" className="text-[#F05A28] hover:underline">
                                                    connect@roadexpressfreight.com
                                                </a>
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <Users className="w-5 h-5 text-[#F05A28] mr-3" />
                                            <div>
                                                <p className="font-medium text-gray-900">Legal Team</p>
                                                <p className="text-gray-600 text-sm">Available for legal inquiries and clarifications</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                        </div>
                    </div>
                </div>

                {/* Download Button */}
                {/* <div className="fixed bottom-8 left-8">
                <button className="flex items-center bg-white text-blue-600 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200">
                    <Download className="w-4 h-4 mr-2" />
                    <span className="font-medium">Download PDF</span>
                </button>
            </div> */}
            </div>
            <Footer />
        </>
    );
};

export default TermsConditionsPage;